import { invokeWithRetry } from './common/invokeWithRetry';

export interface InventoryItem {
  id: string;
  name: string;
  size: string | null;
  stock: number;
  available: number;
  reserved: number;
  price: number;
  category: string;
  gym_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateItemData {
  name: string;
  size?: string;
  stock: number;
  available: number;
  reserved: number;
  price: number;
  category: string;
}

export interface UpdateItemData {
  name?: string;
  size?: string;
  stock?: number;
  available?: number;
  reserved?: number;
  price?: number;
  category?: string;
}

// GET inventory items for a gym
export async function getInventory(gymId: string): Promise<InventoryItem[] | null> {
  const { data, error, authExpired } = await invokeWithRetry<{ items: InventoryItem[] }>(
    'inventory-management',
    { action: 'get_inventory', gym_id: gymId }
  );

  if (authExpired || error) {
    console.error('[inventoryManagementWrapper] getInventory error:', error);
    return null;
  }

  return data?.items ?? [];
}

// GET single inventory item
export async function getInventoryItem(gymId: string, itemId: string): Promise<InventoryItem | null> {
  const { data, error, authExpired } = await invokeWithRetry<{ item: InventoryItem }>(
    'inventory-management',
    { action: 'get_item', gym_id: gymId, itemId }
  );

  if (authExpired || error) {
    console.error('[inventoryManagementWrapper] getInventoryItem error:', error);
    return null;
  }

  return data?.item ?? null;
}

// CREATE inventory item
export async function createInventoryItem(gymId: string, itemData: CreateItemData): Promise<InventoryItem | null> {
  const { data, error, authExpired } = await invokeWithRetry<{ item: InventoryItem }>(
    'inventory-management',
    { action: 'create_item', gym_id: gymId, itemData }
  );

  if (authExpired || error) {
    console.error('[inventoryManagementWrapper] createInventoryItem error:', error);
    return null;
  }

  return data?.item ?? null;
}

// UPDATE inventory item
export async function updateInventoryItem(
  gymId: string, 
  itemId: string, 
  itemData: UpdateItemData
): Promise<InventoryItem | null> {
  const { data, error, authExpired } = await invokeWithRetry<{ item: InventoryItem }>(
    'inventory-management',
    { action: 'update_item', gym_id: gymId, itemId, itemData }
  );

  if (authExpired || error) {
    console.error('[inventoryManagementWrapper] updateInventoryItem error:', error);
    return null;
  }

  return data?.item ?? null;
}

// DELETE inventory item
export async function deleteInventoryItem(gymId: string, itemId: string): Promise<boolean> {
  const { data, error, authExpired } = await invokeWithRetry<{ success: boolean }>(
    'inventory-management',
    { action: 'delete_item', gym_id: gymId, itemId }
  );

  if (authExpired || error) {
    console.error('[inventoryManagementWrapper] deleteInventoryItem error:', error);
    return false;
  }

  return data?.success ?? false;
}
